import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useNotifications, useProposeFinding, useReport, useUpdatePreferences } from '../api/queries';
import { SourcePicker } from '../components/SourcePicker';
import { localToIso } from '../lib/events';
import type { Attribution, EvidenceLink, EvidenceRelation } from '../api/types';
import { useSession } from '../auth/session';
import { findingPath } from '../components/FindingCard';
import { Field, MutationError } from '../components/Form';
import { EmptyState, QueryState } from '../components/States';
import { formatDateTime, relationMeta } from '../lib/format';

const ATTRIBUTION: { value: Attribution; label: string; help: string }[] = [
  { value: 'named', label: 'Credit me by name', help: 'Your display name may appear with your contributions and in reviewed exports.' },
  { value: 'anonymous', label: 'Show my contributions without my name', help: 'Others see what you added, but not who added it.' },
  { value: 'hidden', label: 'Hide my contributions from other members', help: 'Only you and the review process see what you add.' },
];

function AttributionPreferences() {
  const session = useSession();
  const mutation = useUpdatePreferences();
  const [value, setValue] = useState<Attribution | null>(session.attribution);
  const [saved, setSaved] = useState(false);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (value === null) return;
    setSaved(false);
    mutation.mutate({ attribution: value }, { onSuccess: () => setSaved(true) });
  }

  return (
    <section id="attribution" className="panel" aria-labelledby="attribution-title">
      <h2 id="attribution-title" className="panel__title">
        How you are credited
      </h2>
      <p className="panel__lede">
        This controls attribution only. Choosing to be named does not mean you agree with any finding, and staying
        anonymous does not weaken a correction or challenge. Changing it marks existing exports for regeneration.
      </p>
      <form onSubmit={submit} className="form">
        <fieldset className="choice-group">
          <legend className="field__label">Attribution preference</legend>
          {ATTRIBUTION.map((option) => (
            <label key={option.value} className={`choice${value === option.value ? ' choice--selected' : ''}`}>
              <input type="radio" name="attribution" value={option.value} checked={value === option.value} onChange={() => setValue(option.value)} />
              <span className="choice__text">
                <span className="choice__label">{option.label}</span>
                <span className="choice__help">{option.help}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {session.attribution === null && <p className="muted small">You have not chosen yet. Until you do, you are not named.</p>}
        <MutationError error={mutation.error} />
        {saved && (
          <p className="form-success" role="status">
            Preference saved.
          </p>
        )}
        <button type="submit" className="button button--primary" disabled={value === null || mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save preference'}
        </button>
      </form>
    </section>
  );
}

function Notifications() {
  const notifications = useNotifications();
  return (
    <section className="panel" aria-labelledby="notifications-title">
      <h2 id="notifications-title" className="panel__title">
        Review activity
      </h2>
      <QueryState query={notifications} label="notifications">
        {(page) =>
          page.items.length === 0 ? (
            <p className="muted">Nothing new. Decisions and member responses on findings you can see will appear here.</p>
          ) : (
            <ul className="notifications">
              {page.items.slice(0, 20).map((item) => (
                <li key={item.id}>
                  <span className="tag">{(item.type ?? item.kind ?? 'update').replace(/_/g, ' ')}</span>{' '}
                  {item.findingId !== undefined && item.findingId !== null ? (
                    <Link to={findingPath(item.findingId)}>{item.message ?? 'Open the finding'}</Link>
                  ) : (
                    <span>{item.message ?? 'Update'}</span>
                  )}{' '}
                  <span className="muted small">{formatDateTime(item.createdAt)}</span>
                </li>
              ))}
            </ul>
          )
        }
      </QueryState>
    </section>
  );
}

interface EvidenceRow {
  key: number;
  sourceId: string;
  relation: EvidenceRelation;
  note: string;
}

const lines = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');

function ProposeFinding() {
  const report = useReport();
  const mutation = useProposeFinding();
  const now = new Date().toISOString().slice(0, 16);
  const [form, setForm] = useState({
    title: '',
    summary: '',
    chapter: '',
    themes: '',
    scope: '',
    method: '',
    interpretation: '',
    uncertainty: '',
    implications: '',
    counterexamples: '',
    sourceCutoff: now,
  });
  const [rows, setRows] = useState<EvidenceRow[]>([{ key: 1, sourceId: '', relation: 'supports', note: '' }]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<{ id: string; title: string } | null>(null);

  const set = (key: keyof typeof form) => (value: string) => setForm((current) => ({ ...current, [key]: value }));

  function submit(event: FormEvent) {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (form.title.trim() === '') next.title = 'Give the finding a title.';
    if (form.summary.trim() === '') next.summary = 'Summarise what you think the records show.';
    if (form.chapter === '') next.chapter = 'Choose the chapter it belongs to.';
    if (form.scope.trim() === '') next.scope = 'Say which records and period this covers.';
    if (form.method.trim() === '') next.method = 'Say how you read or checked the evidence.';
    if (form.interpretation.trim() === '') next.interpretation = 'Explain your reading of the evidence.';
    if (lines(form.uncertainty).length === 0) next.uncertainty = 'Name at least one thing the evidence cannot establish.';
    const evidence: EvidenceLink[] = rows
      .filter((row) => row.sourceId !== '')
      .map((row) => ({ sourceId: row.sourceId, relation: row.relation, ...(row.note.trim() !== '' ? { note: row.note.trim() } : {}) }));
    if (evidence.length === 0) next.evidence = 'Cite at least one source.';
    // The API requires a full ISO datetime with offset, not a bare date.
    const cutoff = localToIso(form.sourceCutoff);
    if (cutoff === null) next.sourceCutoff = 'Enter when your reading of the sources stops.';
    setErrors(next);
    if (Object.keys(next).length > 0 || cutoff === null) return;
    setCreated(null);
    mutation.mutate(
      {
        title: form.title.trim(),
        summary: form.summary.trim(),
        chapter: form.chapter,
        themes: form.themes
          .split(',')
          .map((theme) => theme.trim().toLowerCase().replace(/\s+/g, '-'))
          .filter((theme) => theme !== ''),
        scope: form.scope.trim(),
        method: form.method.trim(),
        interpretation: form.interpretation.trim(),
        uncertainty: lines(form.uncertainty),
        implications: lines(form.implications),
        counterexamples: lines(form.counterexamples),
        evidence,
        episodeIds: [],
        sourceCutoff: cutoff,
      },
      { onSuccess: (result) => setCreated({ id: result.finding.id, title: result.finding.title }) },
    );
  }

  return (
    <section className="panel" aria-labelledby="propose-title">
      <h2 id="propose-title" className="panel__title">
        Propose a new finding
      </h2>
      <p className="panel__lede">
        Proposals enter the report as <strong>proposed</strong> and are clearly labelled until a steward reviews them.
        Include what complicates your reading as well as what supports it.
      </p>
      {created !== null ? (
        <div className="form-success" role="status">
          <p>
            Proposed “{created.title}”. <Link to={findingPath(created.id)}>Open it</Link> to follow the discussion.
          </p>
          <button type="button" className="button button--secondary" onClick={() => setCreated(null)}>
            Propose another
          </button>
        </div>
      ) : (
        <form className="form form--columns" onSubmit={submit} noValidate>
          <Field label="Title" error={errors.title ?? null}>
            {(props) => <input {...props} className="input" value={form.title} onChange={(event) => set('title')(event.target.value)} />}
          </Field>
          <Field label="Chapter" error={errors.chapter ?? null}>
            {(props) => (
              <select {...props} className="input" value={form.chapter} onChange={(event) => set('chapter')(event.target.value)}>
                <option value="">Choose a chapter</option>
                {(report.data?.chapters ?? []).map((chapter) => (
                  <option key={chapter.id} value={chapter.id}>
                    {chapter.title}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Summary" error={errors.summary ?? null}>
            {(props) => (
              <textarea {...props} className="input textarea" rows={3} value={form.summary} onChange={(event) => set('summary')(event.target.value)} />
            )}
          </Field>
          <Field label="Themes" help="Comma separated, e.g. mentoring, review">
            {(props) => <input {...props} className="input" value={form.themes} onChange={(event) => set('themes')(event.target.value)} />}
          </Field>
          <Field label="Scope" error={errors.scope ?? null} help="Which records, which period, and how they were selected.">
            {(props) => <textarea {...props} className="input textarea" rows={3} value={form.scope} onChange={(event) => set('scope')(event.target.value)} />}
          </Field>
          <Field label="Method" error={errors.method ?? null}>
            {(props) => <textarea {...props} className="input textarea" rows={3} value={form.method} onChange={(event) => set('method')(event.target.value)} />}
          </Field>
          <Field label="Interpretation" error={errors.interpretation ?? null}>
            {(props) => (
              <textarea
                {...props}
                className="input textarea"
                rows={4}
                value={form.interpretation}
                onChange={(event) => set('interpretation')(event.target.value)}
              />
            )}
          </Field>
          <Field label="Uncertainty" error={errors.uncertainty ?? null} help="One point per line.">
            {(props) => (
              <textarea {...props} className="input textarea" rows={4} value={form.uncertainty} onChange={(event) => set('uncertainty')(event.target.value)} />
            )}
          </Field>
          <Field label="Implications" help="One point per line. Optional.">
            {(props) => (
              <textarea {...props} className="input textarea" rows={3} value={form.implications} onChange={(event) => set('implications')(event.target.value)} />
            )}
          </Field>
          <Field label="Counterexamples" help="Records that cut against this reading. One per line.">
            {(props) => (
              <textarea
                {...props}
                className="input textarea"
                rows={3}
                value={form.counterexamples}
                onChange={(event) => set('counterexamples')(event.target.value)}
              />
            )}
          </Field>
          <fieldset className="evidence-rows form__wide">
            <legend className="field__label">Evidence</legend>
            {errors.evidence !== undefined && <p className="field__error">{errors.evidence}</p>}
            {rows.map((row, index) => (
              <div key={row.key} className="evidence-row">
                <SourcePicker
                  label={`Source ${String(index + 1)}`}
                  value={row.sourceId}
                  emptyLabel="No source chosen"
                  onChange={(sourceId) => setRows(rows.map((item) => (item.key === row.key ? { ...item, sourceId } : item)))}
                />
                <label className="field">
                  <span className="field__label">Relation</span>
                  <select
                    className="input"
                    value={row.relation}
                    onChange={(event) =>
                      setRows(rows.map((item) => (item.key === row.key ? { ...item, relation: event.target.value as EvidenceRelation } : item)))
                    }
                  >
                    {Object.entries(relationMeta).map(([relation, meta]) => (
                      <option key={relation} value={relation}>
                        {meta.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field__label">Note</span>
                  <input
                    className="input"
                    value={row.note}
                    onChange={(event) => setRows(rows.map((item) => (item.key === row.key ? { ...item, note: event.target.value } : item)))}
                  />
                </label>
                {rows.length > 1 && (
                  <button
                    type="button"
                    className="button button--link"
                    onClick={() => setRows(rows.filter((item) => item.key !== row.key))}
                  >
                    Remove<span className="visually-hidden"> source {index + 1}</span>
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="button button--secondary"
              onClick={() => setRows([...rows, { key: Math.max(...rows.map((row) => row.key)) + 1, sourceId: '', relation: 'supports', note: '' }])}
            >
              Add another source
            </button>
          </fieldset>
          <Field label="Sources considered up to (UTC)" error={errors.sourceCutoff ?? null}>
            {(props) => (
              <input
                {...props}
                type="datetime-local"
                className="input"
                value={form.sourceCutoff}
                onChange={(event) => set('sourceCutoff')(event.target.value)}
              />
            )}
          </Field>
          <div className="form__wide">
            <MutationError error={mutation.error} />
            <button type="submit" className="button button--primary" disabled={mutation.isPending}>
              {mutation.isPending ? 'Submitting…' : 'Submit proposal'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

/** Entry to the project loop, alongside the research contributions below. */
function ProjectLoopEntry() {
  const session = useSession();
  return (
    <section className="panel panel--loop" aria-labelledby="loop-entry-title">
      <h2 id="loop-entry-title" className="panel__title">
        Bring a project into the loop
      </h2>
      <p className="panel__lede">
        Two selected pilots, GitFitCode Hub and UpAhead MVP, are testing one reviewed cycle: an owner-approved project update, one
        concrete request for help, and a recorded next step. Nothing is posted automatically.
      </p>
      <ul className="link-list">
        <li>
          <Link className="button button--secondary" to="/projects/new">
            Start a new project with a goal
          </Link>
        </li>
        <li>
          <Link className="button button--secondary" to="/pilots">
            See the selected pilots
          </Link>
        </li>
        {session.hasRole('steward') && (
          <>
            <li>
              <Link className="button button--secondary" to="/steward/operations?event=project_update">
                Record a project update
              </Link>
            </li>
            <li>
              <Link className="button button--secondary" to="/steward/operations?event=support_requested">
                Record a support request
              </Link>
            </li>
          </>
        )}
      </ul>
      {!session.hasRole('steward') && (
        <p className="small muted">
          You can start your own project with a goal, or simply follow others; following needs no goal. Stewards can record a support
          request if you need help.
        </p>
      )}
    </section>
  );
}

export function Participate() {
  return (
    <main id="main" tabIndex={-1} className="page">
      <header className="page__header">
        <p className="eyebrow">Participate</p>
        <h1 className="page__title">Add what the records missed</h1>
        <p className="lede">
          The report is meant to be corrected by the people it describes. Challenge or correct any finding from its page;
          use this page to propose something new, choose how you are credited, and follow review activity.
        </p>
      </header>
      <ProjectLoopEntry />
      <div className="participate">
        <div className="participate__main">
          <ProposeFinding />
        </div>
        <div className="participate__side">
          <AttributionPreferences />
          <Notifications />
          <EmptyState title="Challenging a finding?">
            <p>
              Open the finding and use <strong>Add to this finding</strong> in its discussion. <Link to="/explore">Find it in Explore</Link>.
            </p>
          </EmptyState>
        </div>
      </div>
    </main>
  );
}
